// Business Unit *scoping* — "which BUs may use this piece of configuration".
//
// Job types, estimate templates and proposal templates each carry a
// `businessUnitIds` array. Two rules make the array read correctly, and both
// are easy to get wrong in an inline `.includes()`:
//
//  1. An EMPTY array is a WILDCARD — the item belongs to every business unit.
//     It is not "belongs to none". A bare `ids.includes(buId)` would hide every
//     unscoped item the moment a BU is picked, which is the opposite of what
//     tenants who never scoped anything expect.
//  2. NO selection means NO predicate. An unselected business unit does not
//     narrow the list to unscoped items — it applies no filter whatsoever.
//
// Both rules mirror the server-side predicate
// `{ OR: [{ businessUnitIds: { isEmpty: true } }, { businessUnitIds: { has } }] }`.

const BUSINESS_UNIT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Is this URL search value shaped like a business-unit id?
 *
 * A create route's `?businessUnitId=` is arbitrary input. It is typed by hand,
 * it is bookmarked and shared, and — because TanStack merges a parent route's
 * validated search into its children — a list layout's own filter value can
 * arrive at a create route that never had such a param in its URL. The jobs
 * list already keeps a non-id sentinel there (`'all'` for "no filter", see
 * `DEFAULT_JOB_LIST_FILTERS`), so "some other route's filter vocabulary" is a
 * live shape, not a hypothetical one.
 *
 * Whatever survives validation goes on to the server as `selectedBusinessUnitId`,
 * which is `@IsUuid()` — a create route that accepts any string turns a stale
 * link into a 400 on the whole save. So the boundary checks the shape and drops
 * anything else.
 *
 * Deliberately NOT a check against the live unit list: a well-formed id naming
 * an ARCHIVED unit must still reach `useBusinessUnitScopeSelection`, which is
 * what reconciles it away once that list arrives.
 */
export function isBusinessUnitId(value: unknown): value is string {
  return typeof value === 'string' && BUSINESS_UNIT_ID_RE.test(value);
}

/** Does an item scoped to `scopeIds` belong to `businessUnitId`? */
export function matchesBusinessUnitScope(
  scopeIds: readonly string[] | null | undefined,
  businessUnitId: string | null | undefined,
): boolean {
  // Rule 2 — nothing selected, nothing filtered.
  if (!businessUnitId) return true;
  const ids = scopeIds ?? [];
  // Rule 1 — an empty scope is every business unit.
  if (ids.length === 0) return true;
  return ids.includes(businessUnitId);
}

/**
 * Filter a list of BU-scoped items. Returns the SAME array reference when no
 * business unit is selected, so callers memoising on the result stay stable
 * for the (overwhelmingly common) unfiltered case.
 */
export function filterByBusinessUnitScope<T extends { businessUnitIds?: string[] | null }>(
  items: T[],
  businessUnitId: string | null | undefined,
): T[] {
  if (!businessUnitId) return items;
  return items.filter((item) => matchesBusinessUnitScope(item.businessUnitIds, businessUnitId));
}

/**
 * The one business unit a scope array unambiguously names, if any.
 *
 * Used where a 0..n scope has to drive a single-unit filter — a proposal
 * template's own units narrowing the estimate templates its options may import
 * from. Zero units is a wildcard and two or more is a genuine choice between
 * them, so both mean "do not narrow" rather than "pick the first".
 */
export function soleBusinessUnitId(
  scopeIds: readonly string[] | null | undefined,
): string | undefined {
  return scopeIds?.length === 1 ? scopeIds[0] : undefined;
}
